import { Button, PageTitle } from '@pwc/pwc-deals-ui';
import _orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { USER_ROLES, CommentTarget } from '@pwc/pwc-deals-web-common';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import AddInsightButton from 'common/components/AddInsightButton/AddInsightButton.tsx';
import FieldCommentWrapper from 'common/components/FieldCommentWrapper';
import featureFlags from 'common/helpers/featureFlags';

import CalloutBox from '../../../../common/components/CalloutBox/CalloutBox';
import { LinkedContentNavigation } from '../../../../common/components/LinkedContentNavigation/LinkedContentNavigation';
import PageContentLabel from '../../../../common/components/PageContentLabel/PageContentLabel';
import PageSection from '../../../../common/components/PageSection';
import StatusViewer from '../../../../common/components/StatusViewer/StatusViewer';
import EnableForProjectState from '../../../security/EnableForProjectState';
import { selectAnalysisSections } from '../../analysisSlice';
import '../../common/analysis-detail-screen.scss';
import Section from '../Section/Section';
import './AnalysisDetailScreen.scss';
import styles from './AnalysisDetailScreen.module.scss';
import PageAuditFields from '../../../../common/components/PageAuditFields/PageAuditFields';
import { useIsAddingCommentEnabled } from '../../../../common/hooks/useIsAddingCommentEnabled';

const AnalysisDetailScreen = ({
  projectId,
  analysis,
  onEdit,
  viewClient,
  shareWithAll,
  linkedConnections,
  commentingAutoLockContentId,
  disableLinkedContentNavigationLinks = false,
  hideTextModifiedBy = false,
}) => {
  const { sections, isLoading, hasError } = useSelector(selectAnalysisSections);
  const { t } = useTranslation();
  const isAddingCommentEnabled = useIsAddingCommentEnabled();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isClientUser = !isPwcUser;
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const sortedSectionByOrder = _orderBy(sections, [(section) => section.order]);

  const scrollToBottom = () => {
    window.scroll({
      top: document.body.offsetHeight,
      left: 0,
      behavior: 'smooth',
    });
  };

  const statusContent = useMemo(
    () =>
      featureFlags.isVersioningPhase1Enabled
        ? {
            ...analysis,
            modifiedBy: hideTextModifiedBy ? undefined : analysis.modifiedBy,
          }
        : analysis,
    [analysis, hideTextModifiedBy]
  );

  return (
    <>
      <PageContentLabel cssLabel="analysis" label="Analysis" />
      <div className={styles.contents}>
        <div className={styles.details}>
          <div className="section-name">Title</div>
          <FieldCommentWrapper fieldKey={CommentTarget.Title}>
            <PageTitle title={analysis.title} />
          </FieldCommentWrapper>

          <StatusViewer
            projectId={projectId}
            content={statusContent}
            allowDraftPill={!viewClient.isOnClientVersion}
            shareWithAll={shareWithAll}
          />
          {isClientUser && !isThirdPartyMember && (
            <>
              {shareWithAll && (
                <PageAuditFields
                  dateShared={statusContent.dateShared}
                  dateModified={statusContent.dateModified}
                  shareWithAll
                />
              )}
              <div className="check-linked-connections">
                <Button
                  data-testid="popover-confirmation-cancel"
                  onClick={scrollToBottom}
                  className="button-link mr-3"
                >
                  {t('checkConnections')}
                </Button>
              </div>
            </>
          )}
          {analysis.insight && (
            <div className="freeform-analysis-insight-container">
              <CalloutBox
                iconName="light-bulb"
                iconClass="icon fill icon-grey-theme"
                headerLabel={t('insight')}
                text={analysis.insight}
                dataTestId="insight"
                usage="detail"
                enableInlineComment={isAddingCommentEnabled}
                commentTarget={CommentTarget.Insight}
              />
            </div>
          )}
          <EnableForProjectState projectId={projectId}>
            {({ isProjectDisabled }) =>
              !analysis.insight &&
              !isProjectDisabled && (
                <AddInsightButton
                  projectId={projectId}
                  contentThreadId={commentingAutoLockContentId}
                  onClick={onEdit}
                />
              )
            }
          </EnableForProjectState>
          <PageSection label="Description" value={analysis.body} />
        </div>
        <div className={styles.list}>
          <LinkedContentNavigation
            disableAllLinks={disableLinkedContentNavigationLinks}
            projectId={projectId}
            connectedAnalyses={linkedConnections.connectedAnalyses}
            connectedKfas={linkedConnections.connectedKfas}
            hypothesisIndex={1}
            currentContent={analysis}
            parentContentId={analysis.contentId}
            sortByDateModified={false}
            isCommentingAllowedOnThisItem
          />
        </div>
      </div>
      {sortedSectionByOrder.map((section) => (
        <Section
          key={section.order}
          projectId={projectId}
          analysisId={analysis.contentThreadId}
          section={section}
          isLoading={isLoading}
          hasError={hasError}
        />
      ))}
      {isClientUser && (
        <div className="bottom-linked-navigation-client-analysis-detail">
          <LinkedContentNavigation
            disableAllLinks={disableLinkedContentNavigationLinks}
            projectId={projectId}
            connectedAnalyses={linkedConnections.connectedAnalyses}
            connectedKfas={linkedConnections.connectedKfas}
            hypothesisIndex={1}
            currentContent={analysis}
            parentContentId={analysis.contentId}
            sortByDateModified={false}
            isCommentingAllowedOnThisItem
          />
        </div>
      )}
    </>
  );
};

export default AnalysisDetailScreen;
